import type { StageT } from "@/components/common/Stepper";
import type { StepT } from "@/components/common/Stepper";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

type State = {
	steps: StepT[];
	currentStep: string;
};

type Actions = {
	setCurrentStep: (step: string) => void;
	markStepComplete: (step: string) => void;
	updateStepStage: (stepKey: string, stage: StageT) => void;
	nextStep: () => void;
	previousStep: () => void;
	updateEventSteps: (events: string[]) => void;
	getNextStepRoute: (currentStep: string, params: Record<string, any>) => { to: string, params: Record<string, any> } | null;
	getPreviousStepRoute: (currentStep: string, params: Record<string, any>) => { to: string, params: Record<string, any> } | null;
};

const createInitialSteps = (): StepT[] => [
	{
		key: "organisation",
		stage: "current",
		words: "Organisation",
		route: {
			to: "/new/diary/$templateId/organisation",
			params: (prev) => ({ ...prev }),
		},
	},
	{
		key: "trackers",
		stage: "future",
		words: "Questionnaire Schedule",
		route: {
			to: "/new/diary/$templateId/trackers",
			params: (prev) => ({ ...prev }),
		},
	},
	{
		key: "events",
		stage: "future",
		words: "Intervention",
		route: {
			to: "/new/diary/$templateId/events",
			params: (prev) => ({ ...prev }),
		},
	},
	{
		key: "contact",
		stage: "future",
		words: "Contact",
		route: {
			to: "/new/diary/$templateId/contact",
			params: (prev) => ({ ...prev }),
		},
	},
	{
		key: "name",
		stage: "future",
		words: "Name",
		route: {
			to: "/new/diary/$templateId/name",
			params: (prev) => ({ ...prev }),
		},
	},
];

const createEventSteps = (events: string[]): StepT[] => {
	const eventSteps: StepT[] = [];

	for (const event of events) {
		if (event === "injection") {
			eventSteps.push(
				{
					key: "injection",
					stage: "future",
					words: "Injection",
					route: {
						to: "/new/diary/$templateId/events/injection",
						params: (prev) => ({ ...prev }),
					},
				},
				{
					key: "injection-details",
					stage: "future",
					words: "Injection Details",
					route: {
						to: "/new/diary/$templateId/events/injection/details",
						params: (prev) => ({ ...prev }),
					},
				},
			);
		} else if (event === "surgery") {
			eventSteps.push(
				{
					key: "surgery",
					stage: "future",
					words: "Surgery Clinicians",
					route: {
						to: "/new/diary/$templateId/events/surgery",
						params: (prev) => ({ ...prev }),
					},
				},
				{
					key: "surgery-details",
					stage: "future",
					words: "Surgery Details",
					route: {
						to: "/new/diary/$templateId/events/surgery/details",
						params: (prev) => ({ ...prev }),
					},
				},
			);
		}else if (event === "shockwave") {
			eventSteps.push(
				{
					key: "shockwave",
					stage: "future",
					words: "Shockwave Clinicians",
					route: {
						to: "/new/diary/$templateId/events/shockwave",
						params: (prev) => ({ ...prev }),
					},
				},
				{
					key: "shockwave-details",
					stage: "future",
					words: "Shockwave Details",
					route: {
						to: "/new/diary/$templateId/events/shockwave/details",
						params: (prev) => ({ ...prev }),
					},
				},
			);
		} else {
			eventSteps.push({
				key: event.toLowerCase(),
				stage: "future",
				words: event.charAt(0).toUpperCase() + event.slice(1),
				route: {
					to: `/new/diary/$templateId/events/${event.toLowerCase()}`,
					params: (prev) => ({ ...prev }),
				},
			});
		}
	}

	return eventSteps;
};

const initialState: State = {
	steps: createInitialSteps(),
	currentStep: "organisation",
};

export const useTemplateNavigationStore = create<State & Actions>()(
	devtools((set, get) => ({
		...initialState,
		setCurrentStep: (step) => {
			const { steps } = get();
			const currentIndex = steps.findIndex((s) => s.key === step);

			set((state) => ({
				currentStep: step,
				steps: steps.map((s, index) => ({
					...s,
					stage:
						index === currentIndex
							? "current"
							: index < currentIndex
								? "past"
								: "future",
				})),
			}));
		},
		markStepComplete: (step) => {
			const { steps, currentStep } = get();
			const stepIndex = steps.findIndex((s) => s.key === step);
			const currentIndex = steps.findIndex((s) => s.key === currentStep);

			set((state) => ({
				steps: steps.map((s, index) => ({
					...s,
					stage:
						index === currentIndex
							? "current"
							: index < currentIndex || index === stepIndex
								? "past"
								: "future",
				})),
			}));
		},
		updateStepStage: (stepKey, stage) => {
			set((state) => ({
				steps: state.steps.map((s) =>
					s.key === stepKey ? { ...s, stage } : s,
				),
			}));
		},
		nextStep: () => {
			const { currentStep, steps } = get();
			const currentIndex = steps.findIndex((s) => s.key === currentStep);
			const nextStep = steps[currentIndex + 1];

			if (nextStep) {
				set((state) => ({
					currentStep: nextStep.key,
					steps: steps.map((s, index) => ({
						...s,
						stage:
							index === currentIndex + 1
								? "current"
								: index <= currentIndex
									? "past"
									: "future",
					})),
				}));
			}
		},
		previousStep: () => {
			const { currentStep, steps } = get();
			const currentIndex = steps.findIndex((s) => s.key === currentStep);
			const prevStep = steps[currentIndex - 1];

			if (prevStep) {
				set((state) => ({
					currentStep: prevStep.key,
					steps: steps.map((s, index) => ({
						...s,
						stage:
							index === currentIndex - 1
								? "current"
								: index < currentIndex - 1
									? "past"
									: "future",
					})),
				}));
			}
		},
		updateEventSteps: (events) => {
			const baseSteps = createInitialSteps();
			const eventSteps = createEventSteps(events);
			// Get all steps except contact and name
			const stepsBeforeContact = baseSteps.slice(0, -2);
			// Get contact and name steps
			const contactStep = baseSteps[baseSteps.length - 2];
			const nameStep = baseSteps[baseSteps.length - 1];

			const allSteps = [
				...stepsBeforeContact,
				...eventSteps,
				contactStep,
				nameStep,
			] as StepT[];

			set((state) => {
				const currentIndex = allSteps.findIndex(
					(s) => s.key === state.currentStep,
				);
				return {
					steps: allSteps.map((s, index) => ({
						...s,
						stage:
							index === currentIndex
								? "current"
								: index < currentIndex
									? "past"
									: "future",
					})),
				};
			});
		},
		getNextStepRoute: (currentStep, params) => {
			const { steps } = get();
			const idx = steps.findIndex(s => s.key === currentStep);
			const next = steps[idx + 1];
			if (next && next.route && typeof next.route.to === "string" && typeof next.route.params === "function") {
				return { to: next.route.to, params: next.route.params(params) };
			}
			return null;
		},
		getPreviousStepRoute: (currentStep, params) => {
			const { steps } = get();
			const idx = steps.findIndex(s => s.key === currentStep);
			const prev = steps[idx - 1];
			if (prev && prev.route && typeof prev.route.to === "string" && typeof prev.route.params === "function") {
				return { to: prev.route.to, params: prev.route.params(params) };
			}
			return null;
		},
	})),
);
